import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  FileText,
  Clock,
  Edit,
  Trash2,
  MessageSquare,
} from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
  card,
  cardTitle,
  sectionTitle,
  bodySecondary,
  accentBarCard,
  surfaceMuted,
  motionPageTransition,
  motionFadeUp,
} from "../../../../../lib/design-system";
import DropdownMenu from "./DropdownMenu";
import Comment from "./Comment";
import CommentForm from "./CommentForm";

const DiscussionDetail = ({
  discussion,
  onBack,
  onDelete,
  onAddComment,
  onAddReply,
  onDeleteComment,
  loading,
}) => {
  if (!discussion) return null;

  const menuItems = [
    {
      label: "Delete",
      icon: <Trash2 className="w-3.5 h-3.5" />,
      danger: true,
      onClick: () => onDelete(discussion._id),
    },
  ];

  const handleAddComment = async (text, attachments) => {
    await onAddComment(text, attachments);
  };

  return (
    <motion.div {...motionPageTransition} key="discussion-detail">
      <button
        className="flex items-center gap-1 text-sm text-accent1 hover:text-accent1/80 font-medium mb-4 transition-colors"
        onClick={onBack}
      >
        <ChevronLeft className="w-4 h-4" />
        Back to discussions
      </button>

      <motion.div {...motionFadeUp} className={cn(card, "p-4 sm:p-6 mb-6")}>
        <div className="flex justify-between items-start gap-3 mb-4 p-3 -m-3 rounded-sm bg-accent1/5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 rounded-sm bg-accent1/10 text-accent1 flex items-center justify-center text-sm font-bold flex-shrink-0 uppercase">
              {discussion.author.name.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-sm text-gray-900 dark:text-white">
                {discussion.author.name}
              </div>
              <div className={cn(bodySecondary, "flex items-center gap-1")}>
                <Clock className="w-3 h-3" />
                {new Date(discussion.createdAt).toLocaleString()}
              </div>
            </div>
          </div>
          <DropdownMenu items={menuItems} />
        </div>

        <h4 className={cn(sectionTitle, "mb-3 mt-4")}>{discussion.title}</h4>
        <div className="h-1 w-12 bg-accent1 rounded-sm" />
        <div className="mt-3 mb-4 whitespace-pre-line text-sm text-gray-700 dark:text-gray-300 break-words">
          {discussion.content}
        </div>

        {discussion.attachments && discussion.attachments.length > 0 && (
          <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-2">
              Attachments
            </div>
            {discussion.attachments.map((file, index) => (
              <a
                href={file.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                key={index}
                className="block"
              >
                <div
                  className={cn(
                    "flex items-center p-2 rounded-sm mb-1",
                    surfaceMuted,
                    "hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  )}
                >
                  <FileText className="w-4 h-4 text-accent1 mr-2 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {typeof file === "string"
                      ? file
                      : file.name || file.fileName}
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}
      </motion.div>

      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-accent1" />
          <h5 className={cardTitle}>
            Comments{" "}
            <span className="text-accent1 font-black">
              ({discussion.comments ? discussion.comments.length : 0})
            </span>
          </h5>
        </div>
        <div className="h-1 w-8 bg-accent1 rounded-sm mb-4" />

        {discussion.comments &&
          discussion.comments.map((comment) => (
            <Comment
              key={comment._id}
              comment={comment}
              onReply={onAddReply}
              onDelete={onDeleteComment}
              loading={loading}
            />
          ))}

        <div className={cn(card, "p-4 mt-4")}>
          <div className="text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-3">
            Add a Comment
          </div>
          <CommentForm
            placeholder="Share your thoughts..."
            onSubmit={handleAddComment}
            loading={loading}
            buttonLabel="Comment"
          />
        </div>
      </div>
    </motion.div>
  );
};

export default DiscussionDetail;
